'use client'

import { Loader2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'

import { PlanCardCta } from '@/features/billing/components/plan-card'
import { PlansGrid } from '@/features/billing/components/plans-grid'
import { usePlans } from '@/service/billing.hook'

const REGISTER_ROUTE = '/register'
const LOGIN_ROUTE = '/login'

/**
 * Public, unauthenticated pricing page. Renders the live `get_plans` catalog so
 * visitors can compare plans and features without signing in; the plan CTAs send
 * them to sign-up and the footer links to login. Checkout itself stays behind
 * auth (the in-app /billing flow).
 */
export function PublicPricingView() {
  const t = useTranslations('billing')
  const { data: plansData, isLoading, isError } = usePlans()

  const cta = (): PlanCardCta => ({ kind: 'link', href: REGISTER_ROUTE, label: t('getStarted') })

  const hasPlans = !isLoading && !isError && !!plansData && plansData.plans.length > 0

  const header = (
    <header className="flex max-w-xl flex-col gap-2">
      <h1 className="font-display text-foreground text-3xl leading-[1.1] md:text-4xl">
        {t('pricingTitle')}
      </h1>
      <p className="text-foreground/70 text-sm">{t('subtitle')}</p>
    </header>
  )

  return (
    <div className="flex w-full flex-col gap-6">
      {/* The grid owns the heading row once plans load, so the interval toggle can sit
          beside it; until then (loading / error / empty) the heading stands alone. */}
      {!hasPlans && header}

      {isLoading && (
        <div className="text-foreground/60 flex items-center gap-2 py-10 text-sm">
          <Loader2Icon className="size-4 animate-spin" />
          {t('loading')}
        </div>
      )}

      {isError && (
        <div className="border-foreground/10 bg-foreground/[0.03] flex flex-col gap-1 border p-6">
          <span className="text-foreground text-base font-medium">{t('unavailableTitle')}</span>
          <span className="text-foreground/60 text-sm">{t('unavailableBody')}</span>
        </div>
      )}

      {hasPlans && (
        // Logged-out visitors are prospective first-time subscribers, so advertise
        // the trial (the badge still only shows on plans that offer one).
        <PlansGrid plans={plansData.plans} trialEligible cta={cta} header={header} />
      )}

      {!isLoading && !isError && plansData?.plans.length === 0 && (
        <div className="border-foreground/10 bg-foreground/[0.03] flex flex-col gap-1 border p-6">
          <span className="text-foreground text-base font-medium">{t('noPlansTitle')}</span>
          <span className="text-foreground/60 text-sm">{t('noPlansBody')}</span>
        </div>
      )}

      <p className="text-foreground/60 text-sm">
        <Link
          href={LOGIN_ROUTE}
          className="hover:text-foreground underline underline-offset-4 transition-colors"
        >
          {t('signInPrompt')}
        </Link>
      </p>
    </div>
  )
}
